import {
  useEffect,
  useState,
  ForwardRefExoticComponent,
  ComponentProps,
  forwardRef,
  createContext,
  useContext,
  ReactNode,
} from 'react';

// TODO: upgrade react
// import { AnimatePresence, motion, useAnimationControls } from 'motion/react';
import * as RDropdownMenu from '@radix-ui/react-dropdown-menu';
import { withStyles } from '../../../utils';
import styles from './DropdownMenu.module.scss';

const cn = withStyles(styles);

const DropdownMenuContext = createContext<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}>({
  isOpen: false,
  setIsOpen: () => {},
});

type DropdownMenuProps = ComponentProps<typeof RDropdownMenu.Root>;

export default function DropdownMenu(props: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(props.open || false);
  }, [props.open]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    props.onOpenChange?.(open);
  };

  return (
    <DropdownMenuContext.Provider value={{ isOpen, setIsOpen }}>
      <RDropdownMenu.Root {...props} open={isOpen} onOpenChange={handleOpenChange}>
        {props.children}
      </RDropdownMenu.Root>
    </DropdownMenuContext.Provider>
  );
}

const DropdownMenuTrigger = forwardRef<HTMLButtonElement, ComponentProps<typeof RDropdownMenu.Trigger>>(
  (props, ref) => {
    const { isOpen, setIsOpen } = useContext(DropdownMenuContext);

    return (
      <RDropdownMenu.Trigger
        ref={ref}
        {...props}
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      />
    );
  },
);

DropdownMenuTrigger.displayName = 'DropdownMenuTrigger';
DropdownMenu.Trigger = DropdownMenuTrigger;

const Content: ForwardRefExoticComponent<ComponentProps<typeof RDropdownMenu.Content>> = forwardRef(
  ({ children, align = 'start', ...props }, ref) => {
    return (
      <RDropdownMenu.Portal>
        <RDropdownMenu.Content
          ref={ref}
          {...props}
          className={cn('dropdownMenuContentContainer', props.className)}
          sideOffset={8}
          align={align}
        >
          <div className={cn('dropdownMenuContent', 'scrollbar')}>{children}</div>
        </RDropdownMenu.Content>
      </RDropdownMenu.Portal>
    );

    // TODO: upgrade react
    // const { isOpen } = useContext(DropdownMenuContext);

    // return (
    //   <AnimatePresence>
    //     {isOpen && (
    //       <RDropdownMenu.Portal forceMount>
    //         <RDropdownMenu.Content
    //           ref={ref}
    //           {...props}
    //           className={cn('dropdownMenuContentContainer', props.className)}
    //           sideOffset={8}
    //           align={align}
    //           asChild // You might need to remove this
    //         >
    //           <motion.div
    //             // style={{
    //             //   maxHeight: 'var(--maxHeight)',
    //             //   overflow: 'auto', // Add this explicitly to motion.div
    //             // }}
    //             initial={{ scale: 0 }}
    //             animate={{ scale: 1 }}
    //             exit={{
    //               scale: 0,
    //               opacity: 0,
    //               transition: {
    //                 duration: 0.2,
    //                 ease: 'circInOut',
    //               },
    //             }}
    //             transition={{
    //               duration: 0.3,
    //               ease: 'backOut',
    //             }}
    //           >
    //             <div className={cn('dropdownMenuContent', 'scrollbar')}>
    //               {children}
    //               {/* <div className={cn('fade')} /> */}
    //             </div>
    //           </motion.div>
    //         </RDropdownMenu.Content>
    //       </RDropdownMenu.Portal>
    //     )}
    //   </AnimatePresence>
    // );
  },
);

Content.displayName = 'Content';
DropdownMenu.Content = Content;

type OnSelectFn = (
  // e: React.MouseEvent<HTMLDivElement>,
  e: Event,
  helpers: {
    close: () => void;
  },
) => void;

type ItemProps = Omit<ComponentProps<typeof RDropdownMenu.Item>, 'onClick' | 'onSelect'> & {
  onSelect?: OnSelectFn;
};

type ColorScheme = 'default' | 'destructive';

const Item: ForwardRefExoticComponent<
  ItemProps & {
    className?: string;
    description?: ReactNode;
    left?: ReactNode;
    colorScheme?: ColorScheme;
  }
> = forwardRef(({ children, description, left, colorScheme = 'default', onSelect, ...props }, ref) => {
  const { setIsOpen } = useContext(DropdownMenuContext);

  return (
    <RDropdownMenu.Item
      ref={ref}
      {...props}
      onSelect={(e) => {
        if (onSelect) {
          onSelect(e, { close: () => setIsOpen(false) });
        } else {
          // e.preventDefault();
          // handleSelect();
        }
      }}
      asChild
    >
      <div
        className={cn('dropdownMenuItem', `colorScheme-${colorScheme}`, props.className)}
        data-testid={'dropdownMenuItem'}
      >
        {left}
        <div>
          <div className={cn('label')}>{children}</div>
          {description && <div className={cn('description')}>{description}</div>}
        </div>
      </div>
    </RDropdownMenu.Item>
  );

  // TODO: upgrade react
  // const styleRef = useRef<HTMLDivElement | null>(null);
  // const animate = useAnimationControls();

  // async function handleSelect() {
  //   if (styleRef.current) {
  //     const style = window.getComputedStyle(styleRef.current);

  //     const bg = style.getPropertyValue('--hoverBgColor');
  //     const labelTextColor = style.getPropertyValue('--labelTextColor');
  //     const descriptionTextColor = style.getPropertyValue('--descriptionTextColor');

  //     await animate.start({
  //       '--hoverBgColor': '#00000088',
  //       '--labelTextColor': '#fff',
  //       '--descriptionTextColor': '#fff',
  //       transition: {
  //         duration: 0.04,
  //       },
  //     });

  //     await animate.start({
  //       '--hoverBgColor': bg,
  //       '--labelTextColor': labelTextColor,
  //       '--descriptionTextColor': descriptionTextColor,
  //       transition: {
  //         duration: 0.04,
  //       },
  //     });

  //     await new Promise((res) => setTimeout(res, 100));
  //   }

  //   setIsOpen(false);
  // }

  // return (
  //   <RDropdownMenu.Item
  //     ref={ref}
  //     {...props}
  //     onSelect={async (e) => {
  //       if (onSelect) {
  //         onSelect(e, { close: handleSelect });
  //       } else {
  //         e.preventDefault();
  //         handleSelect();
  //       }
  //     }}
  //     asChild
  //   >
  //     <motion.div
  //       ref={styleRef}
  //       animate={animate}
  //       className={cn('dropdownMenuItem', `colorScheme-${colorScheme}`, props.className)}
  //     >
  //       <div>
  //         <div className={cn('label')}>{children}</div>
  //         {description && <div className={cn('description')}>{description}</div>}
  //       </div>

  //       {right}
  //     </motion.div>
  //   </RDropdownMenu.Item>
  // );
});

Item.displayName = 'DropdownMenuItem';
DropdownMenu.Item = Item;

const CustomItem: ForwardRefExoticComponent<ItemProps> = forwardRef(({ onSelect, ...props }, ref) => {
  const { setIsOpen } = useContext(DropdownMenuContext);

  return (
    <RDropdownMenu.Item
      ref={ref}
      {...props}
      className={cn('dropdownMenuCustomItem', props.className)}
      onSelect={(e) => {
        onSelect?.(e, {
          close: () => setIsOpen(false),
        });
      }}
    />
  );
});
CustomItem.displayName = 'DropdownMenuCustomItem';
DropdownMenu.CustomItem = CustomItem;
